import React, { Fragment, useEffect, useMemo, useState, } from 'react';
import { Button, } from 'reactstrap';
import { useToggle } from 'react-use';
import { keyBy, fromPairs, mapValues, pickBy, orderBy, chunk, isEmpty, omit, sumBy, groupBy, uniq, } from 'lodash';
import numeral from 'numeral';
import { useBoolean } from 'react-use';
import fileDownload from 'js-file-download';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import qs from 'qs';
import { format as formatDate } from 'date-fns';
import { toast } from 'react-toastify';
import Toggle from 'react-toggle';

import env from '../../env';
import firebase, { functions } from '../../firebase';
import { idWithTenant, } from '../../shared/util';
import { boxSizes, malls, } from '../../shared/config';
import { batch, getCollectionData, } from '../../shared/firebase';
import { computeInventory, } from '../../shared/models/product';
import { mappingValuesOf, hasMappingValues, productMallMappingId, saveProductMallMapping, } from '../../shared/models/productMallMapping';
import { newId } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionFetch from '../hooks/useCollectionFetch';
import useQueryParams from '../hooks/useQueryParams';
import usePagination from '../hooks/usePagination';
import Page from '../hocs/Page';
import ModelFormModal from '../modals/ModelFormModal';
import ProductInventoryHistoryModal from '../modals/ProductInventoryHistoryModal';
import ExportButton from '../ExportButton';
import Field from '../Field';
import AddButton from '../AddButton';
import DeleteButton from '../DeleteButton';
import BatchImportButton from '../BatchImportButton';
import ProgressButton from '../ProgressButton';
import QuerySortLabel from '../QuerySortLabel';
import QuerySelector from '../QuerySelector';
import QueryBoolean from '../QueryBoolean';
import QueryInput from '../QueryInput';
import Pagination from '../Pagination';
import DataToggle from '../DataToggle';
import ProductsTabs from '../ProductsTabs';
import SimpleLoading from '../SimpleLoading';
import ProductTagsDisplay from '../ProductTagsDisplay';

const countPerPage = 100;
const updateRakutenStocks = functions.httpsCallable('updateRakutenStocks', { timeout: 550000 });
const mapShopifyProducts = functions.httpsCallable('mapShopifyProducts', { timeout: 550000 });
const { keys, values, entries } = Object;
const db = firebase.firestore();
const sortIterateees = {
  id: _ => _.createdAt.toDate(),
};

export default Page(function Products (props) {
  const { user, tenant, history, } = props;
  const queryParams = useQueryParams();
  const shopId = queryParams.shopId?.[0];
  const [rakutenError, setRakutenError] = useState();
  const shops = useCollectionSubscription(tenant.ref.collection('shops').orderBy('createdAt'), [tenant.id]);
  const shopsById = keyBy(shops, 'id');
  const shopOptions = shops.map(_ => ({ label: _.name, value: _.id }));
  const shop = shopsById[shopId];
  const { label: mallLabel, mappingItems } = malls[shop?.mall] ?? {};
  const enabledMalls = pickBy(malls, (v, k) => tenant.tenantOptionValues?.includes(k));
  const onWrote = async () => {
    await tenant.ref.update({ shouldUpdateMallStocks: true });
  };
  const { items: productChunks, isLoading: isLoadingProductChunks } = useCollectionSubscription(tenant.ref.collection('productChunks'), [tenant.id], { detail: true, initialItems: null, });
  const mercariStocksError = useDocumentSubscription(shop?.mall === 'mercari' && shopId && tenant.ref.collection('errors').doc(`mercariStocks_${shopId}`), [tenant.id, shopId, shop?.mall]);
  const chunkProducts = useMemo(() => orderBy(productChunks?.flatMap(_ => entries(omit(_, ['id', 'ref', 'exists'])).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('products').doc(k), ...v, })).map(_ => ({ ..._, fullId: idWithTenant(_.ref) }))), _ => numeral(_.id).value(), 'desc'), [tenant, productChunks]);
  const { items: mappings, isLoading: isLoadingMappings } = useCollectionSubscription(shop && tenant.ref.collection('productMallMappings').where('shopId', '==', shopId), [tenant.id, shopId, shop != null], { detail: true, initialItems: null, });
  const mappingsGroupedByProductId = useMemo(() => groupBy(orderBy(mappings || [], _ => _.createdAt?.toDate()), 'productId'), [mappings]);
  const [editingMapping, setEditingMapping] = useState(null);

  // NOTE: 絞り込み。審査担当者がモール側のSKUで検索することがあるため、紐付けたモールSKUでも引けるようにする
  const filterChunkProducts = () => {
    let filteredChunkProducts = chunkProducts;
    if(queryParams.sku) {
      filteredChunkProducts = filteredChunkProducts.filter(p => p.sku?.includes(queryParams.sku) || (mappingsGroupedByProductId[p.id] || []).some(m => values(mappingValuesOf(shop?.mall, m)).some(_ => _.includes(queryParams.sku))));
    }
    return filteredChunkProducts;
  };

  const filteredChunkProducts = useMemo(() => filterChunkProducts(), [chunkProducts, queryParams.sku, mappingsGroupedByProductId]);
  const pagination = usePagination({ countPerPage, totalCount: filteredChunkProducts.length, });
  const { offset, } = pagination;
  const products = productChunks != null ? filteredChunkProducts.slice(offset, offset + countPerPage) : null;
  const fields = fromPairs(mappingItems?.map((_, i) => [_.name, { label: [mallLabel, _.label].join(' '), type: 'string', validations: i === 0 ? { required: v => !isEmpty(v) } : {}, }]));
  const csvColumnOf = _ => [mallLabel, _.label].join(' ');

  const rowsForExport = async () => {
    const products = await getCollectionData(tenant.ref.collection('products').orderBy('createdAt', 'desc'));
    return products.flatMap((product) => {
      const { id, sku, } = product;
      const productMappings = mappingsGroupedByProductId[id] || [];
      return (productMappings.length > 0 ? productMappings : [{}]).map((mapping) => {
        return {
          ID: `${tenant.id}-${id}`,
          SKU: sku,
          ...fromPairs(mappingItems.map(_ => [csvColumnOf(_), mapping[_.name]])),
        };
      });
    });
  };
  // CSVに出てきた商品は、その店舗の紐付けをCSVの行の内容で置き換える（行が無い商品の紐付けは触らない）
  const processRows = async (rows) => {
    const chunkProductsById = keyBy(chunkProducts, 'id');
    const targetRows = rows.filter(_ => _.ID?.includes('-')).map(_ => ({ ..._, productId: _.ID.slice(_.ID.indexOf('-') + 1) }));
    const unknownIds = uniq(targetRows.filter(_ => chunkProductsById[_.productId] == null).map(_ => _.ID));
    const newMappings = targetRows
      .map(row => ({ productId: row.productId, values: mappingValuesOf(shop.mall, fromPairs(mappingItems.map(_ => [_.name, row[csvColumnOf(_)]]))) }))
      .filter(_ => hasMappingValues(shop.mall, _.values))
      .map(_ => ({ ..._, id: productMallMappingId(shop, _.values) }));
    const targetProductIds = uniq(targetRows.map(_ => _.productId));
    const currentMappings = await getCollectionData(tenant.ref.collection('productMallMappings').where('shopId', '==', shop.id));
    const currentMappingsById = keyBy(currentMappings, 'id');
    const duplicatedMappings = values(groupBy(newMappings, 'id')).filter(_ => _.length > 1).map(_ => _[0]);
    const conflictedMappings = newMappings.filter(_ => currentMappingsById[_.id] != null && currentMappingsById[_.id].productId !== _.productId && !targetProductIds.includes(currentMappingsById[_.id].productId));
    const errorMessages = [
      ...unknownIds.map(_ => `${_}: 商品が見つかりません`),
      ...duplicatedMappings.map(_ => `${values(_.values).filter(_ => _).join(' / ')}: CSV内で複数の商品に紐付いています`),
      ...conflictedMappings.map(_ => `${values(_.values).filter(_ => _).join(' / ')}: 既に別の商品(${tenant.id}-${currentMappingsById[_.id].productId})に紐付いています`),
    ];
    if (errorMessages.length > 0) {
      toast.error(<div style={{ whiteSpace: 'pre-line', }}>{errorMessages.join('\n')}</div>, { autoClose: false });
      throw new Error(errorMessages.join('\n'));
    }

    const newMappingIds = newMappings.map(_ => _.id);
    return [
      ...newMappings.map(_ => ({ ..._, type: 'set', isNew: currentMappingsById[_.id] == null })),
      ...currentMappings.filter(_ => targetProductIds.includes(_.productId) && !newMappingIds.includes(_.id)).map(_ => ({ type: 'delete', id: _.id })),
    ];
  };
  const processRow = (batch, row) => {
    const ref = tenant.ref.collection('productMallMappings').doc(row.id);
    if (row.type === 'delete') return batch.delete(ref);

    // 同じモールSKUの紐付けが既にあれば、在庫連携用にキャッシュしたモール側IDを残す
    batch.set(ref, {
      shopId: shop.id,
      mall: shop.mall,
      productId: row.productId,
      ...row.values,
      ...(row.isNew && { createdAt: new Date() }),
      updatedAt: new Date(),
    }, { merge: true });
  };
  const onSubmitMapping = async (values, { onClickClose }) => {
    try {
      await saveProductMallMapping(tenant, shop, { productId: editingMapping.productId, values, prevMapping: editingMapping.mapping, });
      await onWrote();
      toast.success('保存しました');
      onClickClose();
    } catch (e) {
      console.error(e);
      toast.error(e.message || '失敗しました');
    }
  };
  const onClickDeleteMapping = async (mapping) => {
    if (!window.confirm('この紐付けを削除します。よろしいですか？')) return;

    await mapping.ref.delete();
    await onWrote();
    toast.success('削除しました');
  };
  const onClickUpdateRakutenStocks = async () => {
    if(!window.confirm('楽天在庫連携します。よろしいですか？')) return;

    try {
      setRakutenError(null);
      await updateRakutenStocks({ tenantId: tenant.id, shopId, });
      toast.success('楽天在庫連携しました');
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
      setRakutenError(e);
    }
  };

  const onClickMapShopifyProducts = async () => {
    if(!window.confirm(`「${shop.name}」のShopify商品マッピングを実行します。よろしいですか？`)) return;

    try {
      await mapShopifyProducts({ tenantId: tenant.id, shopId, });
      await onWrote();
      toast.success('マッピングしました');
    } catch(e) {
      console.error('Shopify商品マッピングエラー:', e);
      const errorMessage = e.code === 'internal' && e.message
        ? e.message
        : `失敗しました (${e.code || 'unknown'})`;
      toast.error(errorMessage, { autoClose: 10000 });
    }
  };
  useEffect(() => {
    user.ref.set({ preferences: { productsTab: '/productMallMappings', } }, { merge: true });
  }, []);

  return (
    <div className="product-mall-mappings container-fluid">
      <div className="p-4 bg-white my-4">
        <ProductsTabs activeTab="productMallMappings" products={products || []} shippingRequests={[]} tenant={tenant} />
        <div className="d-flex justify-content-start align-items-end mb-2 gap-1">
          <QuerySelector width={200} paramName="shopId" options={shopOptions} label="店舗" />
          <QueryInput width={300} paramName="sku" label="SKUで検索" />
        </div>
        {
          rakutenError && (
            <div className="alert alert-danger">
              {
                rakutenError?.details?.errors.map(({ code, message }, i) => {
                  return (
                    <div key={i}>
                      {code} {message}
                    </div>
                  );
                })
              }
            </div>
          )
        }
        {
          mercariStocksError?.message && (
            <div className="alert alert-danger">
              <div className="font-weight-bold">
                メルカリ在庫連携でエラーが発生しています
              </div>
              <div style={{ whiteSpace: 'pre-line', }}>
                {mercariStocksError?.message}
              </div>
            </div>
          )
        }
        {
          shop != null && (
            <Fragment>
              <div className="d-flex justify-content-end mb-1 gap-1 align-items-end">
                <Pagination {...pagination} />
                <div className="d-flex align-items-center gap-1 text-nowrap">
                  <ExportButton label="エクスポート" fileName="商品対応表.csv" rows={rowsForExport} />
                  <BatchImportButton processRows={processRows} processRow={processRow} onComplete={onWrote} confirm={_ => window.confirm('CSVに含まれる商品は、この店舗の紐付けがCSVの内容に置き換わります（モールの項目が空の行は紐付けなしとして扱います）。よろしいですか？')} />
                  {
                    shop.mall === 'shopify' && (
                      <ProgressButton process={onClickMapShopifyProducts} disabled={!shop.shopifyAccessToken}>
                        Shopify商品マッピング
                      </ProgressButton>
                    )
                  }
                  {
                    shop.mall === 'rakuten' && (
                      <ProgressButton process={onClickUpdateRakutenStocks}>
                        在庫連携する
                      </ProgressButton>
                    )
                  }
                </div>
              </div>
              <div className="mt-1 overflow-auto position-relative" style={{ zIndex: 0, }}>
                {
                  products?.length > 0 ? (
                    <table className="table table-sm table-bordered sticky-table">
                      <thead className="thead-light text-center">
                        <tr>
                          <th style={{ minWidth: 150 }}>
                            商品ID
                          </th>
                          <th style={{ minWidth: 150 }}>
                            SKU
                          </th>
                          {
                            mappingItems?.map(({ name, label, }) => {
                              return (
                                <th key={name} style={{ minWidth: 150 }}>
                                  {malls[shop?.mall]?.label} {label}
                                </th>
                              );
                            })
                          }
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          products.flatMap((product) => {
                            const { id, ref, sku, } = product;
                            const productMappings = mappingsGroupedByProductId[id] || [];
                            const rowSpan = Math.max(productMappings.length, 1);
                            const productCells = (
                              <Fragment>
                                <td rowSpan={rowSpan}>
                                  {idWithTenant(ref)}
                                </td>
                                <td rowSpan={rowSpan}>
                                  <div>{sku}</div>
                                  <Button size="sm" color="primary" outline className="mt-1" onClick={() => setEditingMapping({ productId: id, mapping: null })}>
                                    <span className="fas fa-plus mr-1" />
                                    紐付けを追加
                                  </Button>
                                </td>
                              </Fragment>
                            );
                            if (productMappings.length === 0) {
                              return [
                                <tr key={id}>
                                  {productCells}
                                  {mappingItems.map(({ name, }) => <td key={name}></td>)}
                                  <td></td>
                                </tr>
                              ];
                            }

                            return productMappings.map((mapping, i) => {
                              return (
                                <tr key={mapping.id}>
                                  {i === 0 && productCells}
                                  {
                                    mappingItems.map(({ name, }, j) => {
                                      return (
                                        <td key={name}>
                                          {mapping[name]}
                                          {j === 0 && mapping.shopifyInventoryItemId != null && <div><span className="badge badge-info">マッピング済み</span></div>}
                                        </td>
                                      );
                                    })
                                  }
                                  <td className="text-right text-nowrap">
                                    <Button color="secondary" onClick={() => setEditingMapping({ productId: id, mapping })}>
                                      <span className="fas fa-edit mr-1" />
                                      編集
                                    </Button>
                                    <Button color="danger" className="ml-1" onClick={() => onClickDeleteMapping(mapping)}>
                                      <span className="fas fa-trash mr-1" />
                                      削除
                                    </Button>
                                  </td>
                                </tr>
                              );
                            });
                          })
                        }
                      </tbody>
                    </table>
                  ) : (
                    <div>
                      {
                        (isLoadingMappings || isLoadingProductChunks) ? (
                          <span className="fas fa-spin fa-spinner" />
                        ) : (
                          <span>商品は未登録です</span>
                        )
                      }
                    </div>
                  )
                }
              </div>
            </Fragment>
          )
        }
      </div>
      {
        editingMapping != null && (
          <ModelFormModal
            isOpen
            title={editingMapping.mapping ? '紐付け 編集' : '紐付け 追加'}
            fields={fields}
            values={editingMapping.mapping ?? {}}
            onClickClose={() => setEditingMapping(null)}
            onSubmit={onSubmitMapping}
            renderFormHeader={() => <div className="mb-2 small text-muted">LOGISP商品: {[tenant.id, editingMapping.productId].join('-')}</div>}
          />
        )
      }
    </div>
  );
});
